import type { ReactNode } from "react";
import { useState } from "react";

import { highlight, tokenise } from "@/lib/highlight";
import { resolveHitUrl } from "@/lib/resolveHitUrl";
import { resolveThumbnail } from "@/lib/thumbnails";
import type { Hit, Mode } from "@ranking-arena/shared/schemas";

import { Badge } from "./Badge";
import { RankChip } from "./RankChip";
import { TypeGlyph, type TypeGlyphKind } from "./TypeGlyph";

interface ResultCardProps {
  hit: Hit;
  rank: number;
  density: "comfortable" | "compact";
  mode: Mode;
  query?: string;
}

/**
 * One Vespa hit rendered as a card. Visuals branch on `mode`:
 *   - doc-search   → DossierV2 layout (TypeGlyph + meta + highlighted title/desc + badges)
 *   - slide-search → FilmstripV2 layout (hero thumbnail + title + deck meta + mini-filmstrip)
 *
 * Both render as `<a target="_blank">` when `resolveHitUrl` returns a URL,
 * or as an inert `<div>` otherwise — graceful degradation for hits without
 * a resolvable source link.
 */
export function ResultCard({ hit, rank, density, mode, query }: ResultCardProps) {
  const url = resolveHitUrl(hit, mode);
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);

  const clickable = url !== null;
  const isFocused = clickable && (hover || focused);

  const outerStyle: React.CSSProperties = {
    border: "1px solid",
    borderColor: isFocused ? "var(--atf-pink-600)" : "var(--border)",
    borderRadius: 10,
    background: "var(--card)",
    boxShadow: isFocused
      ? "0 0 0 3px color-mix(in oklch, var(--atf-pink-600) 15%, transparent)"
      : "var(--shadow-xs)",
    transition: "box-shadow .15s, border-color .15s",
    cursor: clickable ? "pointer" : "default",
    textDecoration: "none",
    color: "inherit",
    outline: "none",
    display: "block",
  };

  const inner: ReactNode =
    mode === "slide-search" ? (
      <SlideCardBody hit={hit} rank={rank} isFocused={isFocused} />
    ) : (
      <DocCardBody hit={hit} rank={rank} query={query} isFocused={isFocused} density={density} />
    );

  if (clickable) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={outerStyle}
      >
        {inner}
      </a>
    );
  }
  return <div style={outerStyle}>{inner}</div>;
}

// ---------- Doc card (Dossier v2) -----------------------------------------

function DocCardBody({
  hit,
  rank,
  query,
  isFocused,
  density,
}: {
  hit: Hit;
  rank: number;
  query?: string;
  isFocused: boolean;
  density: "comfortable" | "compact";
}) {
  const h = hit as unknown as Record<string, unknown>;
  const tokens = tokenise(query);
  const filetype = normaliseFiletype(h.filetype);
  const path = typeof h.path === "string" && h.path ? h.path : derivePath(hit.client);
  const pages = typeof h.pages === "number" ? h.pages : undefined;
  const kind = typeof h.kind === "string" ? h.kind : undefined;
  const small = density === "compact";

  return (
    <div
      style={{
        padding: small ? "12px 14px" : "14px 16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <RankChip rank={rank} highlighted={isFocused} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
            minWidth: 0,
          }}
        >
          <TypeGlyph kind={filetype} />
          <div
            style={{
              fontSize: 11,
              color: "var(--muted-foreground)",
              fontFamily: "var(--font-mono)",
              display: "flex",
              gap: 6,
              alignItems: "center",
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{path}</span>
            {pages !== undefined && (
              <>
                <DotSep />
                <span>{pages}p</span>
              </>
            )}
            {hit.year && (
              <>
                <DotSep />
                <span>{hit.year}</span>
              </>
            )}
          </div>
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1.3,
            letterSpacing: "-0.005em",
          }}
        >
          {highlight(hit.title ?? hit.id, tokens)}
        </div>
        {!small && hit.description && (
          <div
            style={{
              fontSize: 13,
              color: "var(--muted-foreground)",
              marginTop: 6,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {highlight(hit.description, tokens)}
          </div>
        )}
        {(hit.industry || hit.client || kind) && (
          <div
            style={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            {hit.industry && <Badge variant="gray">{hit.industry}</Badge>}
            {hit.client && <Badge variant="outline">{hit.client}</Badge>}
            {kind && <Badge variant="outline">{kind}</Badge>}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Slide card (Filmstrip v2) -------------------------------------

function SlideCardBody({
  hit,
  rank,
  isFocused,
}: {
  hit: Hit;
  rank: number;
  isFocused: boolean;
}) {
  const h = hit as unknown as Record<string, unknown>;
  const primaryUrl = resolveThumbnail("slide-search", h);
  const linkThumbnail = typeof h.link_thumbnail === "string" ? h.link_thumbnail : null;
  const slideNumber = parseSlideNumber(linkThumbnail, h.link_slide);
  const parsedUrl = linkThumbnail ? parseSlideUrl(linkThumbnail) : null;
  const deckLength = typeof h.deckLength === "number" ? h.deckLength : null;

  return (
    <div
      style={{
        padding: 12,
        display: "flex",
        gap: 12,
        alignItems: "stretch",
      }}
    >
      <RankChip rank={rank} highlighted={isFocused} />
      <PrimaryThumb url={primaryUrl} slideNumber={slideNumber} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {hit.title ?? hit.id}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--muted-foreground)",
            fontFamily: "var(--font-mono)",
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
          }}
        >
          <TypeGlyph kind="pptx" />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {hit.client ?? "internal"}
            {hit.year ? ` · ${hit.year}` : ""}
          </span>
        </div>
        {(hit.industry || hit.client || hit.year) && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {hit.industry && <Badge variant="gray">{hit.industry}</Badge>}
            {hit.client && <Badge variant="outline">{hit.client}</Badge>}
            {hit.year && <Badge variant="outline">{hit.year}</Badge>}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {parsedUrl && slideNumber !== null && (
          <FilmStrip parsed={parsedUrl} current={slideNumber} deckLength={deckLength} />
        )}
      </div>
    </div>
  );
}

function PrimaryThumb({
  url,
  slideNumber,
}: {
  url: string | null;
  slideNumber: number | null;
}) {
  const [failed, setFailed] = useState(false);
  const show = url !== null && !failed;
  return (
    <div
      style={{
        width: 200,
        aspectRatio: "16 / 9",
        flexShrink: 0,
        borderRadius: 6,
        overflow: "hidden",
        border: "1px solid var(--border)",
        background: show
          ? "var(--secondary)"
          : "linear-gradient(180deg, var(--secondary), color-mix(in oklch, var(--secondary) 60%, var(--background)))",
        display: "grid",
        placeItems: "center",
        color: "var(--muted-foreground)",
        position: "relative",
      }}
    >
      {show ? (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <PlaceholderDocIcon />
      )}
      {slideNumber !== null && (
        <div
          style={{
            position: "absolute",
            bottom: 6,
            right: 6,
            padding: "2px 6px",
            borderRadius: 3,
            background: "rgba(0,0,0,0.55)",
            color: "white",
            fontFamily: "var(--font-mono)",
            fontSize: 9,
          }}
        >
          slide {slideNumber}
        </div>
      )}
    </div>
  );
}

function FilmStrip({
  parsed,
  current,
  deckLength,
}: {
  parsed: { base: string; pad: number };
  current: number;
  deckLength: number | null;
}) {
  const hasNext = deckLength === null ? false : current < deckLength;
  const hasPrev = current > 1;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        paddingTop: 6,
      }}
    >
      {hasPrev ? (
        <MiniStrip url={neighborUrl(parsed, current - 1)} n={current - 1} />
      ) : (
        <MiniPlaceholder />
      )}
      <div
        style={{
          outline: "2px solid var(--atf-pink-600)",
          outlineOffset: 1,
          borderRadius: 3,
        }}
      >
        <MiniStrip url={neighborUrl(parsed, current)} n={current} />
      </div>
      {hasNext ? (
        <MiniStrip url={neighborUrl(parsed, current + 1)} n={current + 1} />
      ) : (
        <MiniPlaceholder />
      )}
      <div style={{ flex: 1 }} />
      <div
        style={{
          fontSize: 10,
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
        }}
      >
        {deckLength !== null ? `${current}/${deckLength}` : `${current}`}
      </div>
    </div>
  );
}

function MiniStrip({ url, n }: { url: string; n: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      style={{
        width: 40,
        aspectRatio: "16 / 9",
        borderRadius: 3,
        border: "1px solid var(--border)",
        overflow: "hidden",
        position: "relative",
        background: "var(--secondary)",
      }}
    >
      {failed ? (
        <div style={{ width: "100%", height: "100%", background: "var(--secondary)" }} />
      ) : (
        <img
          src={url}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          padding: "0 3px",
          fontFamily: "var(--font-mono)",
          fontSize: 8,
          background: "rgba(0,0,0,0.55)",
          color: "white",
        }}
      >
        {n}
      </div>
    </div>
  );
}

function MiniPlaceholder() {
  return (
    <div
      style={{
        width: 40,
        aspectRatio: "16 / 9",
        borderRadius: 3,
        border: "1px dashed var(--border)",
        background: "var(--secondary)",
      }}
    />
  );
}

// ---------- Helpers -------------------------------------------------------

function DotSep() {
  return <span style={{ color: "var(--border)" }}>·</span>;
}

function PlaceholderDocIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function normaliseFiletype(raw: unknown): TypeGlyphKind {
  if (raw === "docx" || raw === "pdf" || raw === "pptx") return raw;
  return "pptx";
}

function derivePath(client?: string): string {
  const slug = (client ?? "internal").toLowerCase();
  return `/projects/${slug}`;
}

/**
 * Parse the slide number from the image-NN.png URL first (the most precise
 * source), then fall back to the #slide=N fragment on link_slide.
 */
function parseSlideNumber(linkThumbnail: string | null, linkSlide: unknown): number | null {
  if (linkThumbnail) {
    const m = linkThumbnail.match(/\/image-(\d+)\.png(?:\?.*)?$/);
    if (m) {
      const n = Number.parseInt(m[1] ?? "", 10);
      if (Number.isFinite(n)) return n;
    }
  }
  if (typeof linkSlide === "string") {
    const m = linkSlide.match(/#slide=(\d+)$/);
    if (m) {
      const n = Number.parseInt(m[1] ?? "", 10);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/**
 * Break a slide-thumbnail URL into its "everything before the padded index"
 * base + the zero-pad width of the index segment, so we can reconstruct
 * neighbor URLs with different indices. Returns null on non-matching URLs.
 */
function parseSlideUrl(url: string): { base: string; pad: number } | null {
  const m = url.match(/^(.*\/image-)(\d+)\.png(\?.*)?$/);
  if (!m) return null;
  return { base: m[1] ?? "", pad: (m[2] ?? "").length };
}

function neighborUrl(parsed: { base: string; pad: number }, n: number): string {
  return `${parsed.base}${String(n).padStart(parsed.pad, "0")}.png`;
}
